import { validateCpf } from "src-next/shared/utils/cpf";
import type { BatchPersonField } from "./types";

function stripAccents(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function looksCpf(value: string): boolean {
  return validateCpf(value).isValid;
}

function looksSex(value: string): boolean {
  const key = stripAccents(value).trim().toLowerCase();
  return key === "m" || key === "f" || key === "masculino" || key === "feminino";
}

function looksDate(value: string): boolean {
  return /^\d{2}\/\d{2}\/\d{4}$/.test(value.trim());
}

function looksPhone(value: string): boolean {
  let digits = value.replace(/\D/g, "");
  if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) {
    digits = digits.slice(2);
  }
  return digits.length === 10 || digits.length === 11;
}

function looksName(value: string): boolean {
  const text = value.trim();
  if (!text) return false;
  if (looksCpf(text) || looksSex(text) || looksDate(text) || looksPhone(text)) return false;
  return /[a-zA-ZÀ-ÿ]/.test(text);
}

function majorityMatches(samples: string[], predicate: (value: string) => boolean): boolean {
  const nonempty = samples.map((item) => item.trim()).filter(Boolean);
  if (nonempty.length === 0) return false;
  const hits = nonempty.filter(predicate).length;
  return hits * 2 > nonempty.length;
}

/**
 * Completa campos que o nome da coluna não resolveu, olhando o conteúdo.
 */
export function inferMissingColumnsFromSamples(
  indexes: Partial<Record<BatchPersonField, number>>,
  sampleRows: string[][],
): void {
  const mappedIndexes = new Set(Object.values(indexes));
  const columnCount = sampleRows[0]?.length ?? 0;
  const detectors: Array<{ field: BatchPersonField; test: (value: string) => boolean }> = [
    { field: "gender", test: looksSex },
    { field: "birthDate", test: looksDate },
    { field: "cpf", test: looksCpf },
    { field: "phone", test: looksPhone },
    { field: "name", test: looksName },
  ];

  for (const { field, test } of detectors) {
    if (indexes[field] !== undefined) continue;
    for (let column = 0; column < columnCount; column += 1) {
      if (mappedIndexes.has(column)) continue;
      const samples = sampleRows.map((row) => row[column] || "");
      if (
        field === "cpf" &&
        majorityMatches(samples, looksPhone) &&
        !majorityMatches(samples, looksCpf)
      ) {
        continue;
      }
      if (!majorityMatches(samples, test)) continue;
      indexes[field] = column;
      mappedIndexes.add(column);
      break;
    }
  }
}
